<template>
    <BaseDropdownMenu :sections="sections" @select="select" @close-auto-focus="keepDialogFocus">
        <template #trigger>
            <BaseButton ref="trigger" theme="tertiary" size="sm" icon-only :disabled="busy" :aria-label="t('teamPage.memberOptions', { name: member.name })">
                <i class="bi bi-three-dots flex items-center justify-center" aria-hidden="true" />
            </BaseButton>
        </template>
    </BaseDropdownMenu>
    <ConfirmationDialog
        v-model:open="open"
        :title="action === 'make-lead' ? t('teamPage.makeLead.title', { name: member.name }) : t('teamPage.removeConfirmation.title', { name: member.name })"
        :description="description"
        :text="description"
        :confirm-label="confirmLabel"
        :confirm-theme="action === 'make-lead' ? 'primary' : 'danger'"
        :cancel-label="t('teamPerspective.cancel')"
        :confirm-disabled="busy"
        :cancel-disabled="busy"
        :close-on-confirm="false"
        :return-focus-to="trigger"
        dialog-content-class="fs-mask"
        @confirm="confirm">
        <template #content>
            <BaseNotification v-if="error" theme="error" :text="error" hide-dismiss />
        </template>
    </ConfirmationDialog>
</template>
<script setup lang="ts">
import { useFetch } from "~vue/api";
import type { TeamPageNavigation, TeamPerspectiveMember, TeamPerspectiveReport } from "~vue/api/generated/team-perspective";
import BaseButton from "~vue/components/BaseButton.vue";
import BaseDropdownMenu, { type DropdownMenuItem, type DropdownMenuSection } from "~vue/components/BaseDropdownMenu.vue";
import BaseNotification from "~vue/components/BaseNotification.vue";
import ConfirmationDialog from "~vue/components/ConfirmationDialog.vue";
import { withTeamPageLinks } from "~vue/Perspective/teamReportNavigation";
import { TEAM_INTERACTION_SOURCE, teamInteractionRequest, type TeamInteractionSource } from "~vue/Teams/teamInteraction";
import AmplitudeEventNames from "~vue/utils/amplitudeEventNames";
import { logUserInteraction } from "~vue/utils/logUtils";
import { computed, ref, useTemplateRef } from "vue";
import { useI18n } from "vue-i18n";

type Action = "remove" | "make-lead";

const {
    source = TEAM_INTERACTION_SOURCE.TEAM_PAGE,
    member,
    teamName,
    navigation,
} = defineProps<{ source?: TeamInteractionSource; member: TeamPerspectiveMember; teamName: string; navigation: TeamPageNavigation }>();
const emit = defineEmits<{ updated: [report: TeamPerspectiveReport, announcement: string, memberId: string] }>();
const { t } = useI18n();
const open = ref(false);
// The dialog has no trigger of its own, so closing it hands focus back to the "…" that opened it.
const trigger = useTemplateRef<InstanceType<typeof BaseButton>>("trigger");
const action = ref<Action>("remove");
const busy = ref(false);
const error = ref("");
const sections = computed<DropdownMenuSection[]>(() => {
    const items: DropdownMenuItem[] = [];
    if (member.make_lead_url) {
        items.push({ id: "make-lead", type: "button", label: t("teamPage.makeLead.action") });
    }
    if (member.remove_url) {
        items.push({ id: "remove", type: "button", label: t("teamPage.remove"), theme: "danger" });
    }
    return [{ id: "membership", items }];
});
const description = computed(() =>
    action.value === "make-lead"
        ? t("teamPage.makeLead.description", { name: member.name, team: teamName })
        : t("teamPage.removeConfirmation.description", { name: member.name, team: teamName }),
);
const confirmLabel = computed(() => {
    if (action.value === "make-lead") {
        return busy.value ? t("teamPage.makeLead.pending") : t("teamPage.makeLead.action");
    }
    return busy.value ? t("teamPage.removeConfirmation.pending") : t("teamPage.remove");
});
function select(item: DropdownMenuItem) {
    action.value = item.id === "make-lead" ? "make-lead" : "remove";
    error.value = "";
    open.value = true;
    logUserInteraction(AmplitudeEventNames.TEAM_MEMBERSHIP_UPDATE_STARTED, { source, membership_action: action.value === "make-lead" ? "make_lead" : "remove" });
}
// The dialog takes focus as the menu closes; returning it to the trigger would pull it back out.
function keepDialogFocus(event: Event) {
    if (open.value) {
        event.preventDefault();
    }
}
async function confirm() {
    const url = action.value === "make-lead" ? member.make_lead_url : member.remove_url;
    if (!url || busy.value) {
        return;
    }
    busy.value = true;
    error.value = "";
    try {
        const request = action.value === "make-lead" ? useFetch(url, teamInteractionRequest(source)).post() : useFetch(url, teamInteractionRequest(source)).delete();
        const { data, error: failure } = await request.json<TeamPerspectiveReport>();
        if (failure.value || !data.value) {
            throw new Error("Membership update failed");
        }
        open.value = false;
        emit(
            "updated",
            withTeamPageLinks(data.value, navigation),
            action.value === "make-lead" ? t("teamPage.makeLead.success", { name: member.name }) : t("teamPage.removed", { name: member.name }),
            member.id,
        );
    } catch {
        error.value = action.value === "make-lead" ? t("teamPage.makeLead.error") : t("teamPage.removeError");
    } finally {
        busy.value = false;
    }
}
</script>
